import { Component, OnInit, Input, OnChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  SearchService,
  AuthorService,
  OutletService,
  TopicService,
} from 'services/index';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import { head, get, eq, isEmpty, includes, find, toLower } from 'lodash';
import * as actions from 'redux/actions';
import { Notification, FacetSearch, Search } from 'app/models';
import { Paginator } from 'primeng/paginator';
import { Observable } from 'rxjs/Observable';
import { LongTermChart } from 'app/interfaces';
import {
  Content,
  RECENT_SORT,
  SPINNER_ICON,
  STAR_ICON,
  STAR_ICON_FILL,
} from 'app/constants';
import { SovChartProps } from 'app/components/sov-chart/sov-chart.interface';

@Component({
  selector: 'facet-topic-content',
  templateUrl: './facet-topic-content.component.html',
  styleUrls: ['./facet-topic-content.component.scss'],
})
export class FacetTopicContentComponent implements OnInit, OnChanges {
  @Input() contentData: any;
  public searchBody: Search;
  public topicContentPage: number;
  public topicContentTotalPages: number;
  public topicContentPageSize: number;
  public topicContentTotal: number;
  public currentPage: number;
  public topicId: number;
  public isTopicSaved: boolean;
  public currentProject: any;
  public loading: boolean;
  public topic: any;
  public contentItems: any[] = [];
  public relatedTopics: any[];
  public relatedCompanies: any[];
  public relatedProducts: any[];
  public topicKeywords: string;
  public companiesList: any[];
  public topicsList: any[];
  public longTermChart: LongTermChart;
  public coveragePastYear: number;
  public newsAuthors: number;
  public newsItems: number;
  public deltaYearPercent: number;
  public starToggle: string;
  public sortSelected: string;
  public hideFollowing: boolean;
  public sovChartValuesA: SovChartProps;
  public sovChartValuesB: SovChartProps;
  public coverageNewItem: number;
  public hideChartContent: boolean;
  public isAddedingOrIsRemoving: boolean;
  private longHistoryTopicId: number;
  public authorOutletName: string;
  public savedTopic: any;
  public categories: any[] = [];
  public readonly shortcutLabels = ['Primary', 'Secondary'];

  @ViewChild('pp', { static: false }) paginator: Paginator;
  public showChartSov: boolean;
  public parentName: Content;

  constructor(
    private searchService: SearchService,
    private authorService: AuthorService,
    private outletService: OutletService,
    private topicService: TopicService,
    private store: Store<AppState>,
    private router: Router
  ) {
    this.parentName = Content.Topic;
    this.hideChartContent = false;
    this.loading = false;
    this.isTopicSaved = false;
    this.topicKeywords = null;
    this.relatedCompanies = null;
    this.relatedTopics = null;
    this.relatedProducts = null;
    this.longTermChart = null;
    this.longHistoryTopicId = null;
    this.starToggle = STAR_ICON;
    this.coverageNewItem = null;
    this.sovChartValuesA = null;
    this.sovChartValuesB = null;
    this.showChartSov = true;
    this.isAddedingOrIsRemoving = false;
    this.searchBody = null;
    this.authorOutletName = null;
  }

  ngOnInit() {
    this.hideFollowing = false;
    this.topicContentPage = 1;
    this.currentPage = 0;

    this.store.select('topicsRanked').subscribe(({ coveragePreview }) => {
      if (coveragePreview) {
        this.coverageNewItem = coveragePreview.newsItems;
      }
    });

    this.store.select('coverages').subscribe(({ contents }) => {
      if (contents) {
        this.coverageNewItem = contents.totalElements;
      }
    });

    this.store.select('topic').subscribe(({ topic }) => {
      this.topicId = get(topic, 'id', null);
    });

    this.store
      .select('topicDialog')
      .subscribe(({ sort, error, isSidebarTopic }) => {
        this.sortSelected = sort;
        this.hideChartContent = isSidebarTopic;
        this.parentName = isSidebarTopic ? Content.TopicSidebar : Content.Topic;
        if (error) {
          this.store.dispatch(
            actions.showNotification({
              notify: new Notification('Topic not found', 'error', 'tl'),
            })
          );
        }
      });

    this.store.select('search').subscribe(({ searchBody }) => {
      if (searchBody) {
        this.searchBody = searchBody;
      }
    });

    this.store.select('project').subscribe(({ projectSelected }) => {
      if (projectSelected) {
        this.currentProject = projectSelected;
      }
    });
    this.store
      .select('topics')
      .subscribe(
        ({
          topics,
          isAddingTopic,
          isDeletingTopic,
          hasTopicDeleted,
          hasTopicAdded,
          addTopicToProjectError,
          removeTopicToProjectError,
        }) => {
          this.topicsList = topics;
          if (this.topic) {
            this.setTopicSaved();
          }
          if (isAddingTopic || isDeletingTopic) {
            this.starToggle = SPINNER_ICON;
          }
          if (hasTopicAdded) {
            this.setTopicSaved();
          }
          if (hasTopicDeleted) {
            this.setTopicSaved();
          }
          if (addTopicToProjectError) {
            this.store.dispatch(
              actions.showNotification({
                notify: new Notification(
                  `Error adding topic to project`,
                  'error',
                  'tl'
                ),
              })
            );
            this.setTopicSaved();
          }

          if (removeTopicToProjectError) {
            this.store.dispatch(
              actions.showNotification({
                notify: new Notification(
                  `Error removing topic to project`,
                  'error',
                  'tl'
                ),
              })
            );
            this.setTopicSaved();
          }
        }
      );

    this.store.select('category').subscribe(({ categories }) => {
      this.categories = categories || [];
    });

    this.store.select('companies').subscribe(({ companies }) => {
      this.companiesList = companies;
    });

    this.store.select('offCanvas').subscribe(({ isOpen }) => {
      if (!isOpen) {
        if (this.currentPage > 0) {
          this.paginator.changePage(0);
        }
      }
    });
  }

  ngOnChanges(changes: any) {
    if (changes.contentData && changes.contentData.currentValue) {
      const { topic, contentItems, facetSearch } =
        changes.contentData.currentValue;
      this.showChartSov = false;
      this.topic = topic;
      this.searchBody = facetSearch.searchBody;
      const { stats } = topic;
      const { name, query } = topic;
      this.topicKeywords = query ? query : `"${name}"`;
      this.setCoverageSection(stats);
      this.loadLongTermHistory(topic, stats);
      this.getRelatedInformation(topic);
      this.topicContentTotalPages = contentItems.totalPages;
      this.topicContentTotal = contentItems.totalElements;
      this.contentItems = contentItems.content;
      this.topicContentPageSize = facetSearch.size;
      this.currentPage = facetSearch.page;
      this.setTopicSaved();
      this.setSovChartData(stats);
      this.authorOutletName = null;
      this.setAuthorOutletName(facetSearch?.searchBody);
    }
  }

  private setAuthorOutletName(searchBody: Search) {
    const authorId = head(searchBody?.authors);
    const outletId = head(searchBody?.outlets);
    if (authorId) {
      this.authorService.getAuthorById(authorId).subscribe((author) => {
        this.authorOutletName = author?.name;
      });
    } else if (outletId) {
      this.outletService.getOutletById(outletId).subscribe((outlet) => {
        this.authorOutletName = outlet?.name;
      });
    }
  }

  setSovChartData(stats) {
    setTimeout(() => {
      if (stats && this.searchBody) {
        if (this.coverageNewItem) {
          if (eq(stats?.newsItems, this.coverageNewItem)) {
            this.showChartSov = false;
          } else {
            if (
              eq(
                toLower(this.topicKeywords),
                toLower(this.searchBody?.keywords)
              )
            ) {
              this.showChartSov = false;
            } else {
              this.showChartSov = true;
            }
          }
        } else {
          this.showChartSov = false;
        }
        this.sovChartValuesA = {
          chartLabels: [this.topic?.name, this.searchBody?.keywords || ''],
          chartData: [
            stats.newsItems - this.topicContentTotal,
            this.topicContentTotal,
          ],
          chartDataTotal: stats?.newsItems,
          query: {
            value: this.topic?.query || '',
            color: { color: 'black' },
          },
          topicName: this.topic?.name,
          authorOutletName: this.authorOutletName,
          origChart: 'sovChartValuesA',
        };
        this.sovChartValuesB = {
          chartLabels: [this.searchBody?.keywords || '', this.topic?.name],
          chartData: [
            this.coverageNewItem - this.topicContentTotal,
            this.topicContentTotal,
          ],
          chartDataTotal: this.coverageNewItem,
          query: {
            value: this.topic?.query || '',
            color: { color: 'white' },
          },
          topicName: this.searchBody?.keywords,
          authorOutletName: this.authorOutletName,
          origChart: 'sovChartValuesB',
        };
      } else {
        this.showChartSov = false;
      }
    }, 1000);
  }

  private setCoverageSection(stats) {
    if (stats) {
      this.coveragePastYear = stats?.newsItems;
      this.newsAuthors = stats?.newsAuthors;
      this.newsItems = stats?.newsItems;
      this.deltaYearPercent = stats?.deltaYearPercent;
      this.longTermChart = !isEmpty(stats?.longHistory)
        ? {
            longTermSeries: [
              {
                name: 'longterm-history',
                data: stats.longHistory,
              },
            ],
            longTermColor: [stats?.longTrend > 0 ? 'lightgray' : 'lightgray'],
          }
        : null;
    } else {
      this.longTermChart = null;
    }
  }

  private loadLongTermHistory(topic, stats) {
    if (
      this.hideChartContent ||
      !isEmpty(stats?.longHistory) ||
      !topic?.id ||
      eq(this.longHistoryTopicId, topic.id)
    ) {
      return;
    }
    this.longHistoryTopicId = topic.id;
    this.topicService.getTopicHistory(topic.id).subscribe((history) => {
      if (eq(this.topic?.id, topic.id)) {
        this.longTermChart = {
          longTermSeries: [
            {
              name: 'longterm-history',
              data: history?.longHistory,
            },
          ],
          longTermColor: [history?.longTrend > 0 ? 'lightgray' : 'lightgray'],
        };
      }
    });
  }

  public goToExploreRelatedCoverage(topic) {
    const url = this.router?.serializeUrl(
      this.router?.createUrlTree([`/search/topic/${topic.id}/coverage`])
    );
    window.open(url, '_blank');
  }

  private getRelatedInformation(topic) {
    if (topic) {
      const { related } = topic;
      if (related) {
        this.relatedCompanies = related?.companies
          ? related.companies?.content
          : null;
        this.relatedTopics = related.topics ? related.topics?.content : null;
        this.relatedProducts = related?.products
          ? related.products?.content
          : null;
      } else {
        this.relatedCompanies = null;
        this.relatedTopics = null;
        this.relatedProducts = null;
      }
    } else {
      this.relatedCompanies = null;
      this.relatedTopics = null;
      this.relatedProducts = null;
    }
  }

  public sortChange(e) {
    this.sortSelected = e;
    this.topicContentPage = 1;
    this.currentPage = 0;
    this.resetPaginator();
    // this.getCompanyContent();
  }

  getPage(e: any) {
    this.topicContentPage = e.page + 1;
    this.currentPage = e.page;
    this.getCompanyContent();
  }

  getCompanyContent() {
    const facetSearch = new FacetSearch({
      searchBody: new Search({
        ...this.searchBody,
        topics: [this.topicId],
      }),
      sort: this.sortSelected,
      page: this.currentPage,
      size: this.topicContentPageSize,
    });

    this.searchService.searchContent(facetSearch).subscribe((response) => {
      this.topicContentTotalPages = response?.totalPages;
      this.topicContentTotal = response?.totalElements;
      this.contentItems = response?.content;
      // this.scrollTop();
    });
  }

  resetPaginator() {
    if (this.paginator) {
      this.paginator.changePage(0);
    }
  }

  getOutlet$(id): Observable<any> {
    return this.outletService.getOutletWithAuthors(id, this.topicId);
  }

  handleSaveTopic(topic) {
    if (!this.isAddedingOrIsRemoving) {
      this.isAddedingOrIsRemoving = true;
      this.starToggle = SPINNER_ICON;
      if (this.isTopicSaved) {
        this.store.dispatch(
          actions.deleteTopicToProject({
            topic,
            userProject: topic.project,
          })
        );
        this.store.dispatch(actions.deleteTopicFromManager({ topic }));
      } else {
        this.store.dispatch(
          actions.addTopicToProject({
            topic,
            userProject: topic.project,
          })
        );
        this.store.dispatch(actions.addTopicFromDialog({ topic }));
      }
    }
  }

  handleTopicLabel(labelName: string) {
    const category = find(
      this.categories,
      (item) => String(item.name).toLowerCase() === labelName.toLowerCase()
    );
    if (!category || !this.savedTopic) {
      this.store.dispatch(
        actions.showNotification({
          notify: new Notification(
            `The ${labelName} label is not available`,
            'error',
            'tl'
          ),
        })
      );
      return;
    }
    this.isAddedingOrIsRemoving = true;
    this.store.dispatch(
      actions.updateTopic({
        topic: {
          ...this.savedTopic,
          categoryId: this.topicLabel === labelName ? null : category.id,
        },
        userTopicId: this.savedTopic.userTopicId,
        userId: null,
        userProject: this.currentProject,
      })
    );
  }

  handleIgnoreTopic(currentlyIgnored: boolean) {
    if (currentlyIgnored) {
      this.handleSaveTopic(this.topic);
      return;
    }
    const ignore = find(this.categories, (item) => String(item.name).toLowerCase() === 'ignore');
    if (!ignore) {
      this.store.dispatch(actions.showNotification({
        notify: new Notification('The Ignore label is not available', 'error', 'tl'),
      }));
      return;
    }
    this.isAddedingOrIsRemoving = true;
    this.store.dispatch(actions.addTopicToProject({
      topic: { ...this.topic, categoryId: ignore.id },
      userProject: this.currentProject,
    }));
  }

  get isTopicIgnored(): boolean {
    return this.topicLabel === 'Ignore';
  }

  get topicLabel(): string {
    return (
      this.savedTopic?.categoryName || this.savedTopic?.label?.name || null
    );
  }

  onDisplayRelatedTopicContent(topic) {
    const sort = /* this.keywords ? RELEVANT_SORT : */ RECENT_SORT;
    this.store.dispatch(actions.startLoading());
    this.store.dispatch(
      actions.setTopicDialog({
        topicSelected: topic,
        projectSelected: this.currentProject,
        keywords: this.searchBody?.keywords || null,
        sort,
        isSidebarTopic: false,
      })
    );
  }

  onDisplayRelatedCompanyContent(company) {
    const sort = /* this.keywords ? RELEVANT_SORT : */ RECENT_SORT;
    this.store.dispatch(
      actions.setCompanyDialog({
        companyId: company?.id,
        projectSelected: this.currentProject,
        keywords: null,
        sort,
      })
    );
  }

  private setTopicSaved() {
    setTimeout(() => {
      this.savedTopic = find(this.topicsList, (i) => eq(i.id, this.topic.id));
      this.isTopicSaved = !!this.savedTopic;
      this.topic = {
        ...this.topic,
        userTopicId: this.savedTopic?.userTopicId,
      };
      this.starToggle = this.isTopicSaved ? STAR_ICON_FILL : STAR_ICON;
      this.isAddedingOrIsRemoving = false;
    }, 500);
  }

  getYahooFinanceURL(value: any) {
    if (isEmpty(value)) {
      return null;
    } else if (includes(value, 'http')) {
      const q = value.substring(value.lastIndexOf('/') + 1, value.length);
      return `https://finance.yahoo.com/quote/${q}`;
    } else {
      return `https://finance.yahoo.com/quote/${value}`;
    }
  }

  openReportMissingDataModal() {
    this.store.dispatch(actions.showReportDialog());
  }
}
